import { useEffect, useState } from "react";
import axios from "axios";
import HorizontalBenchmarkChart from "./HorizontalBenchmarkChart";
import { useTheme } from "../context/ThemeContext";

const API_BASE_URL = `${(process.env.REACT_APP_API_URL || "").replace(/\/$/, "")}/api`;
const CALCULATED_VALUES_URL = `${API_BASE_URL}/kpi/calculated_values`;

type SelectedKpiRow = {
  category: string;
  kpi: string;
};

type CalculatedKpiRow = {
  kpi_category: string;
  kpi_name: string;
  [key: string]: unknown;
};

type CalculatedKpiRowWithMeta = CalculatedKpiRow & {
  kpi_id: string;
  company_id: number;
};

type BenchMarkingSummaryProps = {
  compareCompanyView: boolean;
};

const getStoredSelectedKpiRows = (): SelectedKpiRow[] => {
  try {
    const stored = localStorage.getItem("selectedKpiRows");
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getStoredCompanyIds = (): number[] => {
  try {
    const stored = localStorage.getItem("filteredCompanyIds");
    const parsedIds = stored ? JSON.parse(stored) : [];

    if (Array.isArray(parsedIds) && parsedIds.length > 0) {
      return [...new Set(parsedIds)].filter(
        (id): id is number => Number.isInteger(id),
      );
    }
  } catch {
    // Ignore malformed localStorage values and use fallback IDs.
  }

  return Array.from({ length: 14 }, (_, index) => index + 1);
};

function BenchMarkingSummary({ compareCompanyView }: BenchMarkingSummaryProps) {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [calculatedData, setCalculatedData] = useState<CalculatedKpiRowWithMeta[]>([]);
  const [selectedKpiRows] = useState<SelectedKpiRow[]>(getStoredSelectedKpiRows);

  useEffect(() => {
    let isMounted = true;

    const fetchSummaryData = async () => {
      setLoading(true);

      try {
        const companyIds = getStoredCompanyIds();
        const calculatedResponses = await Promise.all(
          companyIds.map((companyId) =>
            axios.get(CALCULATED_VALUES_URL, {
              params: { company_id: companyId },
            }),
          ),
        );

        const combinedCalculatedData = calculatedResponses.flatMap(
          (response, companyIndex) => {
            const rows: CalculatedKpiRow[] = Array.isArray(response.data?.rows)
              ? response.data.rows
              : [];
            const companyId = companyIds[companyIndex];

            return rows.map((kpi, rowIndex) => ({
              ...kpi,
              kpi_id: `${companyId}-${rowIndex + 1}`,
              company_id: companyId,
            }));
          },
        );

        if (isMounted) {
          setCalculatedData(combinedCalculatedData);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching benchmarking summary data:", error);
          setCalculatedData([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSummaryData();

    return () => {
      isMounted = false;
    };
  }, []);

  const visibleCalculatedData =
    selectedKpiRows.length > 0
      ? calculatedData.filter((item) =>
          selectedKpiRows.some(
            (row) =>
              row.category === item.kpi_category && row.kpi === item.kpi_name,
          ),
        )
      : calculatedData;

  return (
    <div
      className={`${
        isDarkMode ? "bg-black" : "bg-[#f4fbf6]"
      } min-h-screen w-full relative overflow-hidden`}
    >
      {!isDarkMode && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-16 -left-10 h-56 w-56 rounded-full bg-emerald-100/70 blur-3xl"></div>
          <div className="absolute top-20 right-10 h-48 w-48 rounded-full bg-sky-100/70 blur-3xl"></div>
          <div className="absolute bottom-8 left-1/3 h-44 w-44 rounded-full bg-indigo-100/60 blur-3xl"></div>
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-0 overflow-visible">
        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <div
              className="h-12 w-12 animate-spin rounded-full border-4 border-gray-300 dark:border-gray-700 border-t-indigo-700"
              aria-label="Loading"
            ></div>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
              Loading data...
            </p>
          </div>
        )}

        {!loading && visibleCalculatedData.length > 0 && (
          <HorizontalBenchmarkChart
            compareCompanyView={compareCompanyView}
            calculatedData={visibleCalculatedData}
          />
        )}

        {!loading && visibleCalculatedData.length === 0 && (
          <div className="text-center py-12 text-lg text-gray-600 dark:text-gray-400">
            No data available for the selected criteria.
          </div>
        )}
      </div>
    </div>
  );
}

export default BenchMarkingSummary;